import React, { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { customFormatMessage } from 'utils';
import { config } from 'config/config';
import { useWindowSize } from 'utils';
import { ConsumablesContext } from './consumables';
import {
  CouponTypeEnum,
  ConsumablePointsOperatorEnum,
  CampaignTypes,
} from 'src/shared/enums/consumables.enum';
import getConfig from 'next/config';
import { DiscoveryCampaignsConfig } from 'config/discovery-campaigns';

const { ADVOCACY, RECURRENCE, DISCOVERY } = CouponTypeEnum;
const { MULTIPLY } = ConsumablePointsOperatorEnum;

export interface CouponData {
  id?: string;
  type?: number;
  myCoupons?: boolean;
  firstScan?: boolean;
  consumablePoints?: number;
  bonusPoints?: number;
  operation?: number;
  consumableName?: string;
  consumableImg?: string;
  expiration?: Date;
  couponFromLanding: boolean;
  campaignType?: CampaignTypes;
  hasFamily?: boolean;
}

const ConsumableCoupon = (props?: CouponData): JSX.Element => {
  let {
    id,
    type,
    myCoupons,
    firstScan,
    consumablePoints,
    bonusPoints,
    operation,
    consumableName,
    consumableImg,
    expiration,
    couponFromLanding,
    campaignType,
    hasFamily,
  } = props;

  const { formatMessage } = useIntl();
  const { isSM } = useWindowSize();

  const { DEFAULT_LANGUAGE } = getConfig().publicRuntimeConfig;

  const srcRecurrenceMobile = `${config.APP.MEDIA_CON}Cupon_mobile_blue.png`;
  const srcRecurrenceDesktop = `${config.APP.MEDIA_CON}Cupon_Desktop_blue.png`;
  const srcFirstSampleMobile = `${config.APP.MEDIA_CON}Cupon_primera_muestra.png`;
  const srcFirstSampleDesktop = `${config.APP.MEDIA_CON}Primera_muestra_Desktop.png`;
  const srcRepeatedSampleMobile = `${config.APP.MEDIA_CON}Muestra_repetida_Mobile.png`;
  const srcRepeatedSampleDesktop = `${config.APP.MEDIA_CON}Muestra_repetida_Desktop.png`;
  const srcDiscoveryMobile = `${config.APP.MEDIA_CON}Discovery_coupon_Mobile_v1.png`;
  const srcDiscoveryDesktop = `${config.APP.MEDIA_CON}Discovery_coupon_Desktop_v1.png`;

  const srcRecurrenceMobileSelected = `${config.APP.MEDIA_CON}Recurrencia_Mobile_Focus.png`;
  const srcRecurrenceDesktopSelected = `${config.APP.MEDIA_CON}Recurrencia_Desktop_Focus_Border_Focus.png`;
  const srcFirstSampleMobileSelected = `${config.APP.MEDIA_CON}Primera Muestra_Mobile_Focus.png`;
  const srcFirstSampleDesktopSelected = `${config.APP.MEDIA_CON}Primera Muestra_Desktop_Border_focus.png`;
  const srcRepeatedSampleMobileSelected = `${config.APP.MEDIA_CON}Muestra Repetida_Mobile_Focus.png`;
  const srcRepeatedSampleDesktopSelected = `${config.APP.MEDIA_CON}Muestra Repetida_Border_focus.png`;
  const srcDiscoveryMobileSelected = `${config.APP.MEDIA_CON}Discovery_coupon_Mobile_Focus_v1.png`;
  const srcDiscoveryDesktopSelected = `${config.APP.MEDIA_CON}Cupon_Discovery_Desktop_Focus_v1.png`;

  const isCampaignDiscoverySpecial = (
    couponType: number,
    campaignType: CampaignTypes,
  ): boolean => {
    return (
      couponType === DISCOVERY &&
      campaignType &&
      !!DiscoveryCampaignsConfig?.[campaignType]
    );
  };

  const printDiscoveryTitleCoupon = (): string => {
    const title = !isCampaignDiscoverySpecial(type, campaignType)
      ? 'consumable.coupon.title.discovery'
      : DiscoveryCampaignsConfig?.[campaignType]?.literals?.coupon?.title;
    return formatMessage({ id: title });
  };

  const rightBlockStyle = (myCoupons: boolean): string => {
    let styles = 'couponRightBlock';
    if (myCoupons) return styles.concat(` ${styles}__myCoupons`);
    else return styles.concat(` ${styles}__list`);
  };

  const titleCouponStyles = (myCoupons: boolean): string => {
    let styles = 'origenCoupon';
    if (myCoupons) {
      if (type === DISCOVERY) {
        styles = styles.concat(` ${styles}__myCouponsDiscovery`);
      } else styles = styles.concat(` ${styles}__myCoupons`);
    } else styles = styles.concat(` ${styles}__list`);
    return styles;
  };

  const titleCouponStylesCampaign = (): { color: string } => {
    return isCampaignDiscoverySpecial(type, campaignType)
      ? {
          color:
            DiscoveryCampaignsConfig?.[campaignType]?.styles?.coupon?.title,
        }
      : undefined;
  };

  const pointsInfoStyles = (myCoupons: boolean): string => {
    let styles = 'couponPointsInfo';
    if (myCoupons) {
      styles = styles.concat(` ${styles}__myCoupons`);
    } else styles = styles.concat(` ${styles}__list`);
    return styles;
  };

  const pointsInfoStylesCampaign = (): { color: string } => {
    return isCampaignDiscoverySpecial(type, campaignType)
      ? {
          color:
            DiscoveryCampaignsConfig?.[campaignType]?.styles?.coupon
              ?.pointsInfo,
        }
      : undefined;
  };

  const extraPointsStyle = (myCoupons: boolean): string => {
    let styles = 'couponExtraPoints';
    if (myCoupons) styles = styles.concat(` ${styles}__myCoupons`);
    else styles = styles.concat(` ${styles}__list`);
    return styles;
  };

  const extraPointsStylesCampaign = (): { color: string } => {
    return isCampaignDiscoverySpecial(type, campaignType)
      ? {
          color:
            DiscoveryCampaignsConfig?.[campaignType]?.styles?.coupon
              ?.pointsInfo,
        }
      : undefined;
  };

  const descriptionStyle = (myCoupons: boolean): string => {
    let styles = 'couponDescription';
    if (myCoupons) styles = styles.concat(` ${styles}__myCoupons`);
    else styles = styles.concat(` ${styles}__list`);
    return styles;
  };

  const descriptionStylesCampaign = (): { color: string } => {
    return isCampaignDiscoverySpecial(type, campaignType)
      ? {
          color:
            DiscoveryCampaignsConfig?.[campaignType]?.styles?.coupon
              ?.description,
        }
      : undefined;
  };

  const validityDateStyle = (myCoupons: boolean): string => {
    let styles = 'validityDate';
    if (myCoupons) styles = styles.concat(` ${styles}__myCoupons`);
    else styles = styles.concat(` ${styles}__list`);
    return styles;
  };

  const validityDateStylesCampaign = (): { color: string } => {
    return isCampaignDiscoverySpecial(type, campaignType)
      ? {
          color:
            DiscoveryCampaignsConfig?.[campaignType]?.styles?.coupon
              ?.description,
        }
      : undefined;
  };

  const description = (myCoupons: boolean): string => {
    if (!hasFamily) return 'consumable.coupon.description-not-family';
    if (myCoupons) return 'consumable.coupon.description-my-coupons';
    else return 'consumable.coupon.description.choose-coupon';
  };

  const recurrenceTitle = (myCoupons: boolean): string => {
    if (myCoupons) return 'consumable.coupon.title.recurrence-myCoupons';
    else return 'consumable.coupon.title.recurrence';
  };

  const advocacyTitle = (firstScan: boolean): string => {
    if (firstScan) return 'consumable.coupon.title.first-sample';
    else return 'consumable.coupon.title.repeated-sample';
  };

  const operationPointsText = (operation: number): string => {
    if (operation === MULTIPLY) return 'consumable.coupon.points';
    else return 'consumable.coupon.extra-points';
  };

  const pointsWinText = (
    operation: number,
    consumableBonusPoints: number,
  ): string => {
    if (operation === MULTIPLY) return consumableBonusPoints.toString() + 'x';
    else return consumableBonusPoints.toString();
  };

  const totalPoints = (
    consumablePoints: number,
    consumableBonusPoints: number,
  ): string => {
    if (operation === MULTIPLY)
      return (consumablePoints * consumableBonusPoints).toString();
    else return (consumablePoints + consumableBonusPoints).toString();
  };

  const expirationDate = (expiration: Date): string => {
    return new Date(expiration).toLocaleDateString(DEFAULT_LANGUAGE);
  };

  const couponFormat = (type: number) => {
    switch (type) {
      case ADVOCACY:
        return (
          <div className={rightBlockStyle(myCoupons)}>
            <div className="couponSuperiorText">
              <div className="couponTypologyCoupon">
                <div className={titleCouponStyles(myCoupons)}>
                  {formatMessage({
                    id: advocacyTitle(firstScan),
                  })}
                </div>
              </div>
            </div>
            <div className="couponPoints">
              <div className="margin-text-coupon">
                <div className={pointsInfoStyles(myCoupons)}>
                  {pointsWinText(operation, bonusPoints)}
                </div>
                <div className={extraPointsStyle(myCoupons)}>
                  {formatMessage({
                    id: operationPointsText(operation),
                  })}
                </div>
                <div className={descriptionStyle(myCoupons)}>
                  {customFormatMessage(description(myCoupons), {
                    productName: consumableName,
                    pointsPlus: totalPoints(consumablePoints, bonusPoints),
                    points: consumablePoints,
                  })}
                </div>
              </div>
              <div className={validityDateStyle(myCoupons)}>
                {customFormatMessage('consumable.coupon.validity-date', {
                  validityDate: expirationDate(expiration),
                })}
              </div>
            </div>
          </div>
        );
      case RECURRENCE:
        return (
          <div className={rightBlockStyle(myCoupons)}>
            <div className="couponSuperiorText">
              <div className="couponTypologyCoupon">
                <div className={titleCouponStyles(myCoupons)}>
                  {formatMessage({
                    id: recurrenceTitle(myCoupons),
                  })}
                </div>
              </div>
            </div>
            <div className="couponPoints">
              <div className="margin-text-coupon">
                <div className={pointsInfoStyles(myCoupons)}>
                  {pointsWinText(operation, bonusPoints)}
                </div>
                <div className={extraPointsStyle(myCoupons)}>
                  {formatMessage({
                    id: operationPointsText(operation),
                  })}
                </div>
                <div className={descriptionStyle(myCoupons)}>
                  {customFormatMessage(description(myCoupons), {
                    productName: consumableName,
                    pointsPlus: totalPoints(consumablePoints, bonusPoints),
                    points: consumablePoints,
                  })}
                </div>
              </div>
              <div className={validityDateStyle(myCoupons)}>
                {customFormatMessage('consumable.coupon.validity-date', {
                  validityDate: expirationDate(expiration),
                })}
              </div>
            </div>
          </div>
        );
      case DISCOVERY:
        return (
          <div className={rightBlockStyle(myCoupons)}>
            <div className="couponSuperiorText">
              <div className="couponTypologyCoupon">
                <div
                  className={titleCouponStyles(myCoupons)}
                  style={titleCouponStylesCampaign()}>
                  {printDiscoveryTitleCoupon()}
                </div>
              </div>
            </div>
            <div className="couponPoints">
              <div className="margin-text-coupon">
                <div
                  className={pointsInfoStyles(myCoupons)}
                  style={pointsInfoStylesCampaign()}>
                  {pointsWinText(operation, bonusPoints)}
                </div>
                <div
                  className={extraPointsStyle(myCoupons)}
                  style={extraPointsStylesCampaign()}>
                  {formatMessage({
                    id: operationPointsText(operation),
                  })}
                </div>
                <div
                  className={descriptionStyle(myCoupons)}
                  style={descriptionStylesCampaign()}>
                  {customFormatMessage(description(myCoupons), {
                    productName: consumableName,
                    pointsPlus: totalPoints(consumablePoints, bonusPoints),
                    points: consumablePoints,
                  })}
                </div>
              </div>
              <div
                className={validityDateStyle(myCoupons)}
                style={validityDateStylesCampaign()}>
                {customFormatMessage('consumable.coupon.validity-date', {
                  validityDate: expirationDate(expiration),
                })}
              </div>
            </div>
          </div>
        );
    }
  };

  const couponImg = (
    type: number,
    isSelected: boolean,
    myCoupons: boolean,
    firstScan: boolean,
  ): string => {
    if (isSM) {
      if (type === RECURRENCE) {
        if (!myCoupons && isSelected) return srcRecurrenceMobileSelected;
        else return srcRecurrenceMobile;
      } else if (type === DISCOVERY) {
        if (isCampaignDiscoverySpecial(type, campaignType)) {
          return !myCoupons && isSelected
            ? DiscoveryCampaignsConfig?.[campaignType]?.images?.coupon
                ?.mobileSelected
            : DiscoveryCampaignsConfig?.[campaignType]?.images?.coupon?.mobile;
        }
        if (!myCoupons && isSelected) return srcDiscoveryMobileSelected;
        else return srcDiscoveryMobile;
      } else {
        if (firstScan) {
          if (!myCoupons && isSelected) return srcFirstSampleMobileSelected;
          else return srcFirstSampleMobile;
        } else {
          if (!myCoupons && isSelected) return srcRepeatedSampleMobileSelected;
          else return srcRepeatedSampleMobile;
        }
      }
    } else {
      if (type === RECURRENCE) {
        if (!myCoupons && isSelected) return srcRecurrenceDesktopSelected;
        else return srcRecurrenceDesktop;
      } else if (type === DISCOVERY) {
        if (isCampaignDiscoverySpecial(type, campaignType)) {
          return !myCoupons && isSelected
            ? DiscoveryCampaignsConfig?.[campaignType]?.images?.coupon
                ?.desktopSelected
            : DiscoveryCampaignsConfig?.[campaignType]?.images?.coupon?.desktop;
        }
        if (!myCoupons && isSelected) return srcDiscoveryDesktopSelected;
        else return srcDiscoveryDesktop;
      } else {
        if (firstScan) {
          if (!myCoupons && isSelected) return srcFirstSampleDesktopSelected;
          else return srcFirstSampleDesktop;
        } else {
          if (!myCoupons && isSelected) return srcRepeatedSampleDesktopSelected;
          else return srcRepeatedSampleDesktop;
        }
      }
    }
  };

  const couponSize = (): string => {
    if (myCoupons) return 'containMyCoupons';
    else return 'containList';
  };

  const checkSelectedCoupon = (): boolean => {
    if (couponFromLanding) return false;
    else {
      const {
        selectedCustomerCoupon,
        consumablesStep,
        pointsSummaryStep,
        customerCoupons,
      } = useContext(ConsumablesContext);
      if (consumablesStep === pointsSummaryStep) return false;
      if (customerCoupons.length === 1) return false;
      return id === selectedCustomerCoupon?.id;
    }
  };

  return (
    <div className={couponSize()}>
      {type === DISCOVERY ? (
        <img
          src={couponImg(type, checkSelectedCoupon(), myCoupons, firstScan)}
          alt={consumableName}
        />
      ) : (
        <img
          src={couponImg(type, checkSelectedCoupon(), myCoupons, firstScan)}
          alt={consumableName}
        />
      )}

      <div className="couponImage">
        <img src={consumableImg} />
      </div>
      {couponFormat(type)}
    </div>
  );
};

export default ConsumableCoupon;
